import { 
	createSlice,
	createSelector,
	createAsyncThunk,
	createEntityAdapter
} from '@reduxjs/toolkit'

import {
	envAwarePost,
	dateToAPIStandard
} from '../../../util/util'

export const getSessions = createAsyncThunk(
	'getSessions',
	async payload => await envAwarePost(
		'api/sessions/',
		{action:'getSessions',...payload}
	)
)

export const markSessionComplete = createAsyncThunk(
	'markSessionComplete',
	async payload => await envAwarePost(
		'api/sessions/',
		{actions:['markSessionComplete','getSessions'],id:payload.session_id}
	)
)

export const markSessionIncomplete = createAsyncThunk(
	'markSessionIncomplete',
	async payload => await envAwarePost(
		'api/sessions/',
		{actions:['markSessionIncomplete','getSessions'],id:payload.session_id}
	)
)

export const editSession = createAsyncThunk(
	'editSession',
	async payload => await envAwarePost(
		'api/sessions/',
		{action:'editSession',...payload}
	)
)

export const addSessions = createAsyncThunk(
	'addSessions',
	async payload => await envAwarePost(
		'api/sessions/',
		{actions:['addSessions','getSessions'],...payload}
	)
)

export const removeSession = createAsyncThunk(
	'removeSession',
	async payload => await envAwarePost(
		'api/sessions/',
		{actions:['removeSession','getSessions'],id:payload.session_id}
	)
)

const sessionsAdapter = createEntityAdapter({
	selectId: ent => ent.session_id
})

const clientSessionsAdapter = createEntityAdapter({
	selectId: ent => ent.client.id
})

const slice = createSlice({
	name: 'sessions',
	initialState : {
		status:'init',
		all:sessionsAdapter.getInitialState(),
		//only used for the overview in the owner account
		dateAggregates:[],
		clientAggregates:clientSessionsAdapter.getInitialState(),
		clientSessionsModal:{
			show:false,
			client_id:null
		}
	},
	reducers : {
		setClientSessionsModal: (state,action)=>{
			state.clientSessionsModal.show = action.payload.show
			state.clientSessionsModal.client_id = action.payload.client_id
		}
	},
	extraReducers : {
		[getSessions.pending] : (state,action) => { 
			state.status = 'pending' 
		},
		[getSessions.fulfilled] : (state,action) => {
			state.status = 'idle'
			sessionsAdapter.upsertMany(
				state.all,
				action.payload.data.sessions
			)
			state.dateAggregates = action.payload.data.session_date_aggregates
			state.clientAggregates = clientSessionsAdapter.upsertMany(
				state.clientAggregates,
				action.payload.data.session_client_aggregates
			)
		},
		[markSessionComplete.pending] : (state,action) => {
			const {client_id,session_id} = action.meta.arg
			state.clientAggregates.entities[client_id].sessions.forEach(s=>{
				if(s.session_id===session_id){
					s.is_completed = 1
				}
			})
		},
		[markSessionComplete.fulfilled] : (state,action) => {
			sessionsAdapter.upsertMany(
				state.all,
				action.payload.getSessions.data.sessions
			)
			state.dateAggregates = action.payload.getSessions.data.session_date_aggregates
			state.clientAggregates = clientSessionsAdapter.upsertMany(
				state.clientAggregates,
				action.payload.getSessions.data.session_client_aggregates
			)
		},
		[markSessionIncomplete.pending] : (state,action) => {
			const {client_id,session_id} = action.meta.arg
			state.clientAggregates.entities[client_id].sessions.forEach(s=>{
				if(s.session_id===session_id){
					s.is_completed = 0
				}
			})
		},
		[markSessionIncomplete.fulfilled] : (state,action) => {
			sessionsAdapter.upsertMany(
				state.all,
				action.payload.getSessions.data.sessions
			)
			state.dateAggregates = action.payload.getSessions.data.session_date_aggregates
			state.clientAggregates = clientSessionsAdapter.upsertMany(
				state.clientAggregates,
				action.payload.getSessions.data.session_client_aggregates
			)
		},
		[removeSession.pending] : (state,action) => {
			const {client_id,session_id} = action.meta.arg
			state.clientAggregates.entities[client_id].sessions = 
				state.clientAggregates.entities[client_id].sessions.filter(s=>s.session_id!=session_id)
		},
		[removeSession.fulfilled] : (state,action) => {
			sessionsAdapter.upsertMany(
				state.all,
				action.payload.getSessions.data.sessions
			)
			state.dateAggregates = action.payload.getSessions.data.session_date_aggregates
			state.clientAggregates = clientSessionsAdapter.upsertMany(
				state.clientAggregates,
				action.payload.getSessions.data.session_client_aggregates
			)
		},
		[editSession.pending] : (state,action) => {
			const {id,update:changes} = action.meta.arg
			//update all
			sessionsAdapter.updateOne(state.all,{id,changes})
			//update nested
			//get session 
			const session = state.all.entities[id]
			//pull client id from session
			const {client_id} = session
			//use client id to get the object storing the sessions grouped by client
			const clientSessionData = state.clientAggregates.entities[client_id]
			//loop over the sessions and apply the updates to the affected session
			clientSessionData.sessions.forEach(session=>{
				if(parseInt(session.session_id)===parseInt(id)){
					Object.entries(changes).forEach(([k,v])=>{
						session[k] = v
					})
				}
			})
		},
		[addSessions.fulfilled] : (state,action)=>{
			sessionsAdapter.upsertMany(
				state.all,
				action.payload.getSessions.data.sessions
			)
			state.dateAggregates = action.payload.getSessions.data.session_date_aggregates
			state.clientAggregates = clientSessionsAdapter.upsertMany(
				state.clientAggregates,
				action.payload.getSessions.data.session_client_aggregates
			)
		}
	}
})

export const {setClientSessionsModal} = slice.actions

export const {
	selectAll: selectAllSessions,
	selectById: selectSessionById,
	selectIds: selectSessionIds
} = sessionsAdapter.getSelectors( state => state.sessions.all )

export const {
	selectAll: selectAllClientSessions,
	selectById: selectClientSessionById,
	selectIds: selectClientSessionIds
} = clientSessionsAdapter.getSelectors( state => state.sessions.clientAggregates )

export default slice.reducer